import { useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Flame, MessageCircle, RefreshCw, Loader2, Radio } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Iter201 — same-origin WS URL derived from REACT_APP_BACKEND_URL. The
// backend WS endpoint accepts cookie-based auth as a fallback so we
// don't need to expose the JWT to JS.
const WS_URL = process.env.REACT_APP_BACKEND_URL
  ?.replace("https://", "wss://")
  .replace("http://", "ws://");

/**
 * Iter198 — "Replies today" widget for the Manager Dashboard.
 *
 * Renders the most recent streak-break replies (reps tapping the
 * one-tap "I've got one going tomorrow" push action from Iter197)
 * so managers see engagement without opening Discord.
 *
 * Iter201 — Now subscribes to a WebSocket channel and prepends live
 * `streak_break_reply` events the moment a rep taps the push. Falls
 * back to the initial fetch + manual refresh on WS drop.
 *
 * Manager+ only surface (endpoint enforces server-side; the widget
 * lives on ManagerView which is already role-gated).
 */
export function StreakRepliesWidget() {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/streak-break/replies?limit=10`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setReplies(Array.isArray(d.replies) ? d.replies : []);
    } catch {
      setReplies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter201 — WebSocket subscription for live reply prepending.
  useEffect(() => {
    if (!WS_URL) return undefined;

    const connect = () => {
      try {
        const ws = new WebSocket(`${WS_URL}/api/ws`);
        wsRef.current = ws;

        ws.onopen = () => setWsConnected(true);

        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg.event !== "streak_break_reply" || !msg.data) return;
            setReplies((prev) => {
              // Dedupe by reply_id in case the widget also refetched.
              if (prev.some((r) => r.reply_id === msg.data.reply_id)) return prev;
              return [msg.data, ...prev].slice(0, 20);
            });
          } catch {
            /* ignore malformed frames */
          }
        };

        ws.onclose = (evt) => {
          setWsConnected(false);
          // Auto-reconnect unless it was a clean close or auth failure.
          if (evt.code !== 1000 && evt.code !== 4001) {
            reconnectTimerRef.current = setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          // Let onclose drive the reconnect; nothing to do here.
        };
      } catch {
        reconnectTimerRef.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(1000, "unmount"); } catch { /* noop */ }
        wsRef.current = null;
      }
    };
  }, []);

  // Iter200 (code-review LOW fix) — compare against the viewer's LOCAL
  // date, not UTC. Reply timestamps come back as UTC ISO strings; we
  // convert each to a local YYYY-MM-DD before matching so managers in
  // negative-UTC timezones don't see phantom over/under counts near
  // midnight.
  const localDateKey = (iso) => {
    try {
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    } catch { return ""; }
  };
  const todayKey = localDateKey(new Date().toISOString());
  const todayReplies = replies.filter((r) => localDateKey(r.created_at) === todayKey);

  return (
    <div
      className="bg-[#111114] border border-white/10 rounded-2xl p-4 sm:p-6 space-y-4"
      data-testid="streak-replies-widget"
    >
      <div className="flex items-center gap-2">
        <MessageCircle className="w-4 h-4 text-emerald-400" />
        <h3 className="text-white font-semibold text-base sm:text-lg">Replies Today</h3>
        <span className="text-xs text-gray-500">from streak-break pushes</span>
        <div className="ml-auto flex items-center gap-2">
          {wsConnected && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400"
              data-testid="streak-replies-live"
              title="Live updates on"
            >
              <Radio className="w-2.5 h-2.5 animate-pulse" />
              live
            </span>
          )}
          <span className="text-[11px] text-gray-500" data-testid="streak-replies-count">
            {loading ? "" : `${todayReplies.length} today`}
          </span>
          <Button
            size="sm" variant="ghost"
            onClick={load}
            disabled={loading}
            data-testid="streak-replies-refresh"
            className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/10"
            title="Refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="streak-replies-loading">
          {[0, 1].map((i) => (
            <div key={i} className="h-14 rounded-lg bg-white/5 border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : todayReplies.length === 0 ? (
        <div
          className="text-center py-6 px-4 border border-dashed border-white/10 rounded-xl"
          data-testid="streak-replies-empty"
        >
          <Flame className="w-5 h-5 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No replies yet today.</p>
          <p className="text-xs text-gray-500 mt-1">
            Reps who tap &ldquo;I&apos;ve got one going tomorrow&rdquo; on the streak-break push land here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {todayReplies.map((r) => {
            let when = "";
            try { when = formatDistanceToNow(new Date(r.created_at), { addSuffix: true }); }
            catch { when = ""; }
            const initial = (r.rep_name || "?").charAt(0).toUpperCase();
            return (
              <div
                key={r.reply_id}
                data-testid={`streak-reply-row-${r.reply_id}`}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2"
              >
                <Avatar className="w-8 h-8 border border-white/10 shrink-0">
                  <AvatarFallback className="bg-gradient-to-br from-emerald-700 to-emerald-600 text-white text-xs">
                    {initial}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white font-medium truncate">
                      {r.rep_name || "A rep"}
                    </span>
                    {r.streak_length > 0 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-300 shrink-0"
                        title={`Was on a ${r.streak_length}-day streak`}
                      >
                        <Flame className="w-2.5 h-2.5" />
                        {r.streak_length}d
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {r.message || "I've got one going tomorrow"}
                  </div>
                </div>
                {when && (
                  <span className="text-[10px] text-gray-500 shrink-0">{when}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
